import { Delete, EditNote, Palette, Publish } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components/Button/Button';
import { DurationInput } from '@/components/DurationInput';
import { Input } from '@/components/Input';
import { InputWrapper } from '@/components/InputWrapper/InputWrapper';
import { ChangeQueueColorModal } from '@/features/changeQueueColor';
import { getFieldsConfig } from '@/features/DetailsDrawer/config/fields';
import { DetailsContent } from '@/features/DetailsDrawer/ui/DetailsContent';
import { TaskLoading } from '@/features/DetailsDrawer/ui/TaskLoading';
import { getProcessedFields } from '@/features/DetailsDrawer/utils/processFields';
import { buttonSx } from '@/features/log/form/constants/styles';
import { TimeButtonsSection } from '@/features/log/form/ui';
import { colorStore } from '@/features/queueColors';
import { useTaskStore } from '@/features/Task';
import { DraftTimelog } from '@/types';
import { formatDurationIso } from '@/utils/formatDurationIso';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { getTrackerUrl } from '@/utils/getTrackerUrl';
import { parseTimeExpression } from '@/utils/parseTimeInput';

import { PublishTargetMenu } from '../PublishTargetMenu';
import { TaskQueueSelector } from '../TaskQueueSelector';
import { useDraftTimelogStore } from '../../model';

type DraftFormValues = {
  taskKey: string | null;
  taskTitle: string | null;
  queueKey: string | null;
  queueName: string | null;
  duration: string;
  date: string;
  startTime: string;
  comment: string;
};

type DraftFormProps =
  | {
      mode: 'create';
      day?: DateTime | null;
      initialStartTime?: string;
      initialDuration?: string;
      onClose: () => void;
    }
  | {
      mode: 'edit';
      draft: DraftTimelog;
      onClose: () => void;
    };

const parseDraftStart = (startStr: string): DateTime => {
  const dt = DateTime.fromISO(startStr);

  return dt.isValid ? dt : DateTime.local();
};

const getInitialValues = (props: DraftFormProps): DraftFormValues => {
  if (props.mode === 'edit') {
    const start = parseDraftStart(props.draft.start);

    return {
      taskKey: props.draft.taskKey,
      taskTitle: props.draft.taskTitle,
      queueKey: props.draft.queueKey,
      queueName: props.draft.queueName,
      duration: formatDurationRussian(props.draft.durationMinutes),
      date: start.toISODate() ?? DateTime.local().toISODate() ?? '',
      startTime: start.toFormat('HH:mm'),
      comment: props.draft.comment ?? '',
    };
  }

  return {
    taskKey: null,
    taskTitle: null,
    queueKey: null,
    queueName: null,
    duration: props.initialDuration ?? '',
    date: props.day?.toISODate() ?? DateTime.local().toISODate() ?? '',
    startTime: props.initialStartTime ?? '',
    comment: '',
  };
};

export const DraftForm = observer((props: DraftFormProps) => {
  const store = useDraftTimelogStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const initialValues = useMemo(() => getInitialValues(props), [props]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DraftFormValues>({
    defaultValues: initialValues,
  });

  const watched = watch();

  const handleSave = async (values: DraftFormValues) => {
    const durationMinutes = parseTimeExpression(values.duration);

    if (!durationMinutes || durationMinutes <= 0) {
      return;
    }

    setIsSubmitting(true);

    const [hours, minutes] = values.startTime
      ? values.startTime.split(':').map((item) => Number(item))
      : [12, 0];

    const start = DateTime.fromISO(values.date)
      .set({ hour: hours ?? 12, minute: minutes ?? 0, second: 0, millisecond: 0 })
      .toISO();

    if (!start) {
      setIsSubmitting(false);

      return;
    }

    const payload = {
      duration: formatDurationIso(durationMinutes),
      start,
      comment: values.comment ?? '',
      taskKey: values.taskKey,
      taskTitle: values.taskTitle,
      queueKey: values.queueKey,
      queueName: values.queueName,
    };

    if (props.mode === 'edit') {
      await store.updateDraft(props.draft.id, payload);
    } else {
      await store.createDraft(payload);
    }

    setIsSubmitting(false);
    props.onClose();
  };

  const isEditWithTask = props.mode === 'edit' && !!watched.taskKey;
  const { loadTask, task, loadingStage: taskLoadingStage } = useTaskStore();
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);

  useEffect(() => {
    if (isEditWithTask && watched.taskKey) {
      loadTask(watched.taskKey);
    }
  }, [isEditWithTask, watched.taskKey]);

  const fieldsConfig = useMemo(() => getFieldsConfig(), []);

  const dummyLog = useMemo(() => {
    if (!isEditWithTask || props.mode !== 'edit') return null;

    const start = parseDraftStart(props.draft.start);

    return {
      id: 0,
      taskKey: props.draft.taskKey ?? '',
      taskTitle: props.draft.taskTitle ?? '',
      durationMinutes: props.draft.durationMinutes,
      start: props.draft.start,
      date: start.toISODate() ?? '',
      comment: props.draft.comment ?? '',
    } as any;
  }, [isEditWithTask, props]);

  const timeFields = useMemo(
    () => (dummyLog ? getProcessedFields(fieldsConfig, dummyLog, task, 'time') : []),
    [fieldsConfig, dummyLog, task],
  );

  const generalFields = useMemo(
    () => (dummyLog ? getProcessedFields(fieldsConfig, dummyLog, task, 'general') : []),
    [fieldsConfig, dummyLog, task],
  );

  const taskKey = watched.taskKey;
  const queue = taskKey?.split('-')[0] ?? null;
  const queueColor = queue ? colorStore.getColorByPrefix(queue) : undefined;
  const needsBorder = queue ? colorStore.needsBorder(queue) : false;

  const formContent = (
    <>
      <TaskQueueSelector
        value={{
          taskKey: watched.taskKey,
          taskTitle: watched.taskTitle,
          queueKey: watched.queueKey,
          queueName: watched.queueName,
        }}
        onChange={(next) => {
          setValue('taskKey', next.taskKey ?? null);
          setValue('taskTitle', next.taskTitle ?? null);
          setValue('queueKey', next.queueKey ?? null);
          setValue('queueName', next.queueName ?? null);
        }}
        onClear={() => {
          setValue('taskKey', null);
          setValue('taskTitle', null);
          setValue('queueKey', null);
          setValue('queueName', null);
        }}
        autoFocus={props.mode === 'create'}
      />

      <InputWrapper label="Затрачено времени" hint="Формат: 1ч 30м, 1:30, 90, 1 30, 1д 2ч 30м">
        <Box sx={{ display: 'flex', gap: '5px' }}>
          <DurationInput control={control} name="duration" error={errors.duration} required />
        </Box>
      </InputWrapper>

      <Box
        sx={{
          minHeight: '25px',
        }}
      >
        <TimeButtonsSection
          currentValue={watch('duration') || ''}
          onChange={(value) => setValue('duration', value)}
        />
      </Box>

      <InputWrapper label="Дата">
        <Controller
          control={control}
          name="date"
          rules={{ required: 'Укажите дату' }}
          render={({ field }) => (
            <Input {...field} type="date" error={!!errors.date} helperText={errors.date?.message} />
          )}
        />
      </InputWrapper>

      <InputWrapper label="Время начала">
        <Controller
          control={control}
          name="startTime"
          render={({ field }) => <Input {...field} value={field.value ?? ''} type="time" />}
        />
      </InputWrapper>

      <InputWrapper label="Комментарий">
        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Введите комментарий (необязательно)"
              multiline
              rows={4}
            />
          )}
        />
      </InputWrapper>

      <DialogActions
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: 1.5,
          p: 0,
        }}
      >
        <Button
          sx={{ ...buttonSx, width: '100%' }}
          variant="outlined"
          onClick={props.onClose}
          disabled={isSubmitting || isPublishing || isDeleting}
        >
          <p>Отмена</p>
        </Button>
        {props.mode === 'edit' && (
          <Button
            sx={{ ...buttonSx, width: '100%' }}
            variant="contained"
            color="error"
            disabled={isSubmitting || isPublishing || isDeleting}
            onClick={async () => {
              if (props.mode !== 'edit') return;

              setIsDeleting(true);
              await store.deleteDraft(props.draft.id);
              setIsDeleting(false);
              props.onClose();
            }}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <Delete />}
          >
            <p>Удалить</p>
          </Button>
        )}
        <Button
          sx={{ ...buttonSx, width: '100%' }}
          variant="contained"
          onClick={handleSubmit(handleSave)}
          disabled={isSubmitting || isPublishing || isDeleting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          <p>{props.mode === 'edit' ? 'Сохранить' : 'Создать'}</p>
        </Button>
        {props.mode === 'edit' && (
          <PublishTargetMenu
            onPublish={async (target) => {
              setIsPublishing(true);
              await store.publishDraft(props.draft.id, target);
              setIsPublishing(false);
              props.onClose();
            }}
          >
            {(open) => (
              <Button
                sx={{ ...buttonSx, width: '100%' }}
                variant="contained"
                disabled={!watched.taskKey || isSubmitting || isPublishing || isDeleting}
                title={!watched.taskKey ? 'Укажите задачу для публикации' : 'Опубликовать черновик'}
                onClick={open}
                startIcon={isPublishing ? <CircularProgress size={16} /> : <Publish />}
              >
                <p>Опубликовать</p>
              </Button>
            )}
          </PublishTargetMenu>
        )}
      </DialogActions>
    </>
  );

  if (isEditWithTask && props.mode === 'edit') {
    return (
      <>
        <Dialog
          open
          onClose={props.onClose}
          maxWidth="md"
          fullWidth
          sx={{
            '.MuiPaper-root': {
              borderRadius: '8px',
              borderTop: '4px solid #f59e0b',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: isEditWithTask ? 2 : 0 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: '18px',
                color: 'var(--primary-color)',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              {watched.taskTitle || 'Редактирование черновика'}
            </Typography>
            <Chip
              icon={<EditNote sx={{ fontSize: 16 }} />}
              label="Черновик"
              size="small"
              sx={{
                bgcolor: '#fef3c7',
                color: '#92400e',
                fontWeight: 600,
                fontSize: 12,
                flexShrink: 0,
                '& .MuiChip-icon': { color: '#d97706' },
              }}
            />
          </Box>

          <Divider />

          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', minHeight: 450, maxHeight: '75vh' }}>
              {/* Left panel — task info */}
              <Box
                sx={{
                  width: '45%',
                  borderRight: 1,
                  borderColor: 'divider',
                  p: 3,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {taskKey ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Ключ задачи
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                        minWidth: 0,
                        flexGrow: 1,
                      }}
                    >
                      <Typography
                        component="a"
                        href={getTrackerUrl(taskKey)}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          textDecoration: 'none',
                          wordBreak: 'break-all',
                          color: queueColor,
                          '&:hover': { textDecoration: 'underline' },
                          ...(needsBorder && {
                            textShadow:
                              '0 0 1px var(--shadow-color-strong), 0 0 1px var(--shadow-color-strong)',
                          }),
                        }}
                      >
                        {taskKey}
                      </Typography>
                      {queue && (
                        <Button
                          variant="text"
                          onClick={() => setIsColorModalOpen(true)}
                          sx={{ minWidth: 'auto', p: 0.5, ml: 1, color: 'text.secondary' }}
                        >
                          <Palette sx={{ fontSize: 20 }} />
                        </Button>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <TaskLoading isLoading={true} />
                )}

                <DetailsContent
                  isLoading={taskLoadingStage.isLoading}
                  task={task}
                  timeFields={timeFields}
                  generalFields={generalFields}
                  loadingStage={taskLoadingStage}
                />
              </Box>

              {/* Right panel — form */}
              <Box
                sx={{
                  width: '55%',
                  p: 3,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {formContent}
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        {queue && (
          <ChangeQueueColorModal
            open={isColorModalOpen}
            onClose={() => setIsColorModalOpen(false)}
            queue={queue}
          />
        )}
      </>
    );
  }

  return (
    <Dialog
      open
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '.MuiPaper-root': {
          borderRadius: '8px',
          borderTop: '4px solid #f59e0b',
        },
      }}
    >
      <DialogContent sx={{ display: 'flex', paddingTop: 0, flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 2,
          }}
        >
          <Typography
            component="h1"
            variant="h6"
            sx={{
              margin: 0,
              fontWeight: 700,
              fontSize: '18px',
              color: 'var(--primary-color)',
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            {props.mode === 'edit' ? 'Редактирование черновика' : 'Создание черновика'}
          </Typography>
          <Chip
            icon={<EditNote sx={{ fontSize: 16 }} />}
            label="Черновик"
            size="small"
            sx={{
              bgcolor: '#fef3c7',
              color: '#92400e',
              fontWeight: 600,
              fontSize: 12,
              '& .MuiChip-icon': { color: '#d97706' },
            }}
          />
        </Box>
        {formContent}
      </DialogContent>
    </Dialog>
  );
});
